import { forwardRef } from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import { Field } from "@/ui/components/form/Field";
import { Fieldset } from "@/ui/components/form/Fieldset";
import { useFieldset } from "@/ui/components/form/Fieldset/useFieldset";
import { Label } from "@/ui/components/form/Label";
import { RadioGroupItem } from "@/ui/components/form/RadioGroup/RadioGroupItem";
import { TextBody } from "@/ui/components/typography/TextBody";
import { cn } from "@/ui/lib/utils";
import {
  RADIO_GROUP_DEFAULTS,
  RADIO_GROUP_OPTION_VARIANT_CLASS_MAP,
  type TRadioGroupElement,
  type TRadioGroupProps,
} from "@/ui/components/form/RadioGroup/RadioGroup.config";

export const RadioGroup = forwardRef<TRadioGroupElement, TRadioGroupProps>(
  (
    {
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-labelledby": ariaLabelledBy,
      className,
      disabled,
      error,
      label,
      options,
      required,
      hideLabel,
      variant = RADIO_GROUP_DEFAULTS.variant,
      ...props
    },
    ref,
  ) => {
    const { describedBy, labelId, errorId } = useFieldset({
      ariaDescribedBy,
      error,
    });

    return (
      <Fieldset
        disabled={disabled}
        error={error}
        errorId={errorId}
        label={label}
        labelId={labelId}
        required={required}
        hideLabel={hideLabel}
      >
        <RadioGroupPrimitive.Root
          {...props}
          ref={ref}
          aria-describedby={describedBy}
          aria-invalid={error ? true : ariaInvalid}
          aria-labelledby={ariaLabelledBy ?? labelId}
          data-component="RadioGroup"
          disabled={disabled}
          required={required}
          className={cn(
            "w-full grid gap-2",
            disabled && "cursor-not-allowed",
            className,
          )}
        >
          { options.map((option) => (
            <Field
              key={option.value}
              data-component="RadioGroupOption"
              className={cn(
                "p-2 rounded-sys-md cursor-pointer grid grid-cols-[theme(spacing.4)_1fr] gap-x-3 shadow-xs",
                (disabled || option.disabled) && "cursor-not-allowed opacity-70",
                RADIO_GROUP_OPTION_VARIANT_CLASS_MAP[variant],
                error && "border border-solid border-component-input-destructive",
              )}
            >
              <RadioGroupItem
                value={option.value}
                disabled={option.disabled}
              />

              <Label
                className={cn(
                  "col-start-2 row-start-1 peer-disabled:opacity-70",
                  variant === "boxed" && "peer-data-[state=checked]:text-foreground-inverse",
                )}
              >
                { option.label }
              </Label>

              { option.description ? (
                <div
                  className={cn(
                    "col-start-2 row-start-2 pt-1.5",
                    "text-foreground-muted",
                    variant === "boxed" && "peer-data-[state=checked]:text-foreground-inverse",
                  )}
                >
                  <TextBody
                    size={2}
                    color="inherit"
                    isResponsive={false}
                  >
                    { option.description }
                  </TextBody>
                </div>
              ) : null }
            </Field>
          )) }
        </RadioGroupPrimitive.Root>
      </Fieldset>
    );
  },
);

RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;
